import { useLocation } from "react-router-dom";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import { useDispatch, useSelector } from "react-redux";
import {
  setProjectDataState,
  setStepDefinitionState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwrightCSharpSpecflowSlices";
import { playwrightCSharpSpecflowProjectWithToolCalling } from "../../../../../../../../../../../services/webServices";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { useContext, useState } from "react";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import SuccessPopup from "../../../../../shared/SuccessPopup";
import StepDefinition from "../../../../../shared/StepDefinition";
import { Box, Button } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const StepDefinitionPlaywrightCSharpSpecflow = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  const { locationKey } = location.state;

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  const {
    featureFiles,
    pageClass,
    stepDefinition,
    projectData,
    automationPath,
  } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpSpecflow"
      ] || {}
  );

  const environment = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "environmentData"
      ] || {}
  );

  const [successPopup, setSucessPopup] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleStepDefinitionUpdate = (updatedData) => {
    dispatch(setStepDefinitionState(locationKey, updatedData));
  };

  const handleGenerateProject = async () => {
    if (!featureFiles || !pageClass) {
      setMessage("Missing required data for project generation");
      setMessagePopup(true);
      return;
    }

    let progressHandler = null;

    try {
      // Initialize progress bar
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating step definitions...");

      // Call API to generate project
      const response = await playwrightCSharpSpecflowProjectWithToolCalling({
        featureFileGenerated: featureFiles,
        pageClassData: pageClass,
        stepDefinitionData: stepDefinition,
        testEnvironmentData: environment,
      });

      dispatch(setProjectDataState(locationKey, response.data));

      if (response.data) {
        setSucessPopup(true);
      }
    } catch (error) {
      setMessage(
        error?.response?.data?.error ||
          "An error occurred while generating the step definitions."
      );
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });

      if (progressHandler) {
        progressHandler.stop();
      }
    }
  };

  const handleSuccessPopupClose = () => {
    setSucessPopup(false);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const testData = projectData["Test Data"];
      const appsettings = projectData["Configuration"]["appsettings"];
      const runsettings = projectData["ProjectMetadata"]["runsettings"];
      const csproj = projectData["ProjectMetadata"]["csproj"];
      const readme = projectData["Documentation"]["README.md"];

      const utilityFile = `using Microsoft.Playwright;
using System;
using System.Linq;
using System.Threading.Tasks;

namespace Utilities
{
    public class Utility
    {
        private readonly IPage _page;

        public Utility(IPage page)
        {
            _page = page ?? throw new ArgumentNullException(nameof(page));
        }

        #region Navigation

        public async Task GoToUrlAsync(string url)
        {
            if (string.IsNullOrWhiteSpace(url))
                throw new ArgumentException("URL cannot be null or empty", nameof(url));

            await _page.GotoAsync(url, new PageGotoOptions
            {
                WaitUntil = WaitUntilState.NetworkIdle
            });
        }

        public Task<string> GetPageTitleAsync() => _page.TitleAsync();

        public Task<string> GetCurrentUrlAsync() => Task.FromResult(_page.Url);

        #endregion

        #region Element Interaction

        public async Task ClickAsync(string selector)
        {
            try
            {
                await _page.Locator(selector).ClickAsync();
            }
            catch (Exception ex)
            {
                throw new InvalidOperationException($"Failed to click on element '{selector}'.", ex);
            }
        }

        public async Task TypeAsync(string selector, string text, bool clear = true)
        {
            var element = _page.Locator(selector);
            if (clear)
                await element.FillAsync(""); // clear existing text
            await element.TypeAsync(text);
        }

        public async Task FillAsync(string selector, string value)
        {
            await _page.Locator(selector).FillAsync(value);
        }

        public async Task SelectDropdownByValueAsync(string selector, string value)
        {
            await _page.Locator(selector).SelectOptionAsync(new SelectOptionValue { Value = value });
        }

        public async Task SelectDropdownByTextAsync(string selector, string text)
        {
            await _page.Locator(selector).SelectOptionAsync(new SelectOptionValue { Label = text });
        }

        public async Task HoverAsync(string selector)
        {
            await _page.Locator(selector).HoverAsync();
        }

        public async Task CheckCheckboxAsync(string selector)
        {
            await _page.Locator(selector).CheckAsync();
        }

        public async Task UncheckCheckboxAsync(string selector)
        {
            await _page.Locator(selector).UncheckAsync();
        }

        #endregion

        #region Element Retrieval

        public async Task<string> GetTextAsync(string selector)
        {
            return await _page.Locator(selector).InnerTextAsync();
        }

        public async Task<string> GetAttributeAsync(string selector, string attributeName)
        {
            return await _page.Locator(selector).GetAttributeAsync(attributeName);
        }

        public async Task<bool> IsVisibleAsync(string selector, int timeout = 5000)
        {
            try
            {
                var locator = _page.Locator(selector);

                // Wait until element is attached and visible
                await locator.WaitForAsync(new LocatorWaitForOptions
                {
                    State = WaitForSelectorState.Visible,
                    Timeout = timeout
                });

                return await locator.IsVisibleAsync();
            }
            catch (TimeoutException)
            {
                return false; // Element not visible within timeout
            }
        }

        public async Task<bool> IsEnabledAsync(string selector)
        {
            return await _page.Locator(selector).IsEnabledAsync();
        }

        #endregion

        #region Waits

        public async Task WaitForSelectorAsync(string selector, int timeout = 5000)
        {
            await _page.WaitForSelectorAsync(selector, new PageWaitForSelectorOptions
            {
                State = WaitForSelectorState.Attached,
                Timeout = timeout
            });
        }

        public async Task WaitForElementToBeVisibleAsync(string selector, int timeout = 5000)
        {
            await _page.WaitForSelectorAsync(selector, new PageWaitForSelectorOptions
            {
                Timeout = timeout,
                State = WaitForSelectorState.Visible
            });
        }

        public async Task WaitForPageLoadAsync()
        {
            await _page.WaitForLoadStateAsync(LoadState.NetworkIdle);
        }

        #endregion

        #region JavaScript Helpers

        public async Task ScrollToElementAsync(string selector)
        {
            await _page.Locator(selector).ScrollIntoViewIfNeededAsync();
        }

        public async Task ExecuteJsAsync(string script, object arg = null)
        {
            await _page.EvaluateAsync(script, arg);
        }

        #endregion

        #region Screenshots

        public async Task TakeScreenshotAsync(string filePath)
        {
            await _page.ScreenshotAsync(new PageScreenshotOptions
            {
                Path = filePath,
                FullPage = true
            });
        }

        #endregion

        #region Alerts / Dialogs

        public void HandleDialog(string action = "accept", string inputText = "")
        {
            _page.Dialog += async (_, dialog) =>
            {
                if (action.ToLower() == "accept")
                    await dialog.AcceptAsync(inputText);
                else
                    await dialog.DismissAsync();
            };
        }

        #endregion

        #region Frames / Windows

        public IFrame GetFrameByName(string frameName)
        {
            return _page.Frames.FirstOrDefault(f => f.Name == frameName);
        }

        public IFrame SwitchToFrame(string frameName)
        {
            var frame = GetFrameByName(frameName);
            if (frame == null)
                throw new Exception($"Frame {frameName} not found");
            return frame;
        }

        public IBrowserContext GetContext()
        {
            return _page.Context;
        }

        #endregion
    }
}
`;

      const zip = new JSZip();

      const featureFolder = zip.folder("Playwright_C_Sharp_Specflow/Features");
      featureFolder.file("FeatureFile.feature", featureFiles);

      const pageClassFolder = zip.folder("Playwright_C_Sharp_Specflow/Pages");
      if (typeof pageClass !== "string" && Array.isArray(pageClass)) {
        pageClass.map((eachClass) => {
          Object.entries(eachClass).map(([key, value]) => {
            pageClassFolder.file(`${key}.cs`, value);
          });
        });
      } else {
        pageClassFolder.file("PageClass.cs", pageClass);
      }

      const resourceFolder = zip.folder(
        "Playwright_C_Sharp_Specflow/Resources"
      );
      resourceFolder.file("TestData.json", testData);

      const stepDefinitionFolder = zip.folder(
        "Playwright_C_Sharp_Specflow/StepDefinitions"
      );
      if (typeof stepDefinition !== "string" && Array.isArray(stepDefinition)) {
        stepDefinition.map((eachClass) => {
          Object.entries(eachClass).map(([key, value]) => {
            stepDefinitionFolder.file(`${key}.cs`, value);
          });
        });
      } else {
        stepDefinitionFolder.file("StepDefinition.cs", stepDefinition);
      }

      const utilitiesFolder = zip.folder(
        "Playwright_C_Sharp_Specflow/Utilities"
      );
      utilitiesFolder.file("Utility.cs", utilityFile);

      zip.file("Playwright_C_Sharp_Specflow/appsettings.json", appsettings);
      zip.file(
        "Playwright_C_Sharp_Specflow/Playwright_C_Sharp_Specflow.csproj",
        csproj
      );
      zip.file(
        "Playwright_C_Sharp_Specflow/Playwright_C_Sharp_Specflow.runsettings",
        runsettings
      );
      zip.file("Playwright_C_Sharp_Specflow/README.md", readme);

      zip.generateAsync({ type: "blob" }).then((content) => {
        saveAs(content, "Playwright_C_Sharp_Specflow");
      });
    } catch (error) {
      setMessage("Unable to download the project. Please try again later");
      setMessagePopup(true);
    } finally {
        setIsDownloading(false);
    }
  };

  const handlePushToGitHub = () => {};

  const handleNavigateBack = () => navigate(-1);

  return (
    <Box sx={{ height: "100%" }}>
      {/* Step Definition */}
      <StepDefinition
        stepDefinition={stepDefinition}
        handleStepDefinition={handleStepDefinitionUpdate}
      />
      {/* Navigation Buttons */}
      <Box
        sx={{
          p: 4,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleNavigateBack}
          sx={{
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "Manrope",
            borderRadius: "8px",
            borderWidth: "2px",
            px: 3,
            py: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "&:hover": {
              borderColor: "#0078D4",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
              borderWidth: "2px",
            },
          }}
        >
          Back
        </Button>

        {/* Continue Button */}
        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={handleGenerateProject}
          disabled={!pageClass || pageClass.length === 0}
          sx={{
            backgroundColor: "#0094FB",
            color: "white",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "Manrope",
            borderRadius: "8px",
            px: 3,
            py: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "&:hover": {
              backgroundColor: "#0078D4",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          Generate Project
        </Button>
      </Box>

      {/* Open Project Sucess Popup */}
      {successPopup && (
        <SuccessPopup
          open={successPopup}
          handleClose={handleSuccessPopupClose}
          projectName={"Playwright C-Sharp Specflow"}
          projectDetails={{"Framework": "Playwright C-Sharp Specflow", "Feature Files": "Generated", "Locators": "Generated", "Page Class Files": "Generated", "Step Definition Files": "Generated", "Project Structure": "Generated"}}
          onDownload={handleDownload}
          onPushToGitHub={handlePushToGitHub}
          isDownloading={isDownloading}
        />
      )}
    </Box>
  );
};

export default StepDefinitionPlaywrightCSharpSpecflow;
